<script>
    import Loader from "@/components/Loader"
    import PremiumBanner from "@/components/PremiumBanner";
    import Emoji from "@/components/util/Emoji.vue";
    import CategoryTreeItem from "@/components/CategoryTreeItem.vue";
    import ImageCropper from "@/components/ImageCropper.vue";
    import {baseURL} from '@/config/variables'

    export default {
        name: 'Categories',
        components: {Emoji, Loader, PremiumBanner, CategoryTreeItem, ImageCropper },

        data: function () {
            return {
                categories: [],
                originalCategories: null,
                category: {},
                showCategory: false,
                loading: true,
                showEmoji: false,
                hasUnsavedChanges: false,
                allExpanded: false,
                expandedCategoryIds: new Set(), // Track which categories are expanded
                bu: baseURL,
                ak: localStorage.apiKey,
                categoryImageFile: '',
                uploadingCategoryImage: false,
                navType: 'general',
                users: []
            }
        },

        mounted: function () {
            this.refresh();
            this.$http.post('/user/get-user-list')
                .then(response => {
                    this.users = response.data.users;
                });
        },

        watch: {
            categories: {
                handler: function() {
                    if (this.originalCategories) {
                        this.markAsChanged();
                    }
                    // Keep expandedCategoryIds in sync with actual expanded state
                    this.syncExpandedIds();
                },
                deep: true
            }
        },

        methods: {
            refresh: function(preserveExpandedState = false) {
                this.loading = true;

                this.$http.post('/category/get-categories-tree', {})
                    .then(response => {
                        this.categories = response.data.categories;

                        // Apply expanded state from expandedCategoryIds Set
                        this.setExpandedRecursiveInPlace(
                            this.categories,
                            preserveExpandedState ? this.expandedCategoryIds : false
                        );

                        this.originalCategories = JSON.parse(JSON.stringify(this.categories));
                        this.hasUnsavedChanges = false;
                        this.loading = false;
                    });
            },

            syncExpandedIds() {
                // Keep expandedCategoryIds in sync with category.expanded properties
                const newIds = new Set();
                const traverse = (cats) => {
                    cats.forEach(cat => {
                        if (cat.expanded) {
                            newIds.add(cat.categoryId);
                        }
                        if (cat.children?.length) {
                            traverse(cat.children);
                        }
                    });
                };
                traverse(this.categories);
                this.expandedCategoryIds = newIds;
            },

            mapTreeRecursive: function(categories, transformer) {
                return categories.map(cat => {
                    const newCat = transformer(cat);
                    if (newCat.children && newCat.children.length) {
                        newCat.children = this.mapTreeRecursive(newCat.children, transformer);
                    }
                    return newCat;
                });
            },

            setExpandedRecursiveInPlace(categories, expanded) {
                categories.forEach(category => {
                    // expanded can be boolean or Set
                    const shouldExpand = expanded instanceof Set
                        ? expanded.has(category.categoryId)
                        : expanded;
                    this.$set(category, 'expanded', shouldExpand);
                    if (category.children?.length) {
                        this.setExpandedRecursiveInPlace(category.children, expanded);
                    }
                });
            },

            toggleAll() {
                this.allExpanded = !this.allExpanded;
                this.setExpandedRecursiveInPlace(this.categories, this.allExpanded);
            },

            removeExpandedFromTree: function(categories) {
                return this.mapTreeRecursive(categories, cat => {
                    // eslint-disable-next-line no-unused-vars
                    const {expanded, ...rest} = cat;
                    return rest;
                });
            },

            saveCategoryTree: function() {
                const tree = this.removeExpandedFromTree(this.categories);
                this.$http.post('/category/edit-categories-tree', {categories: tree})
                    .then(() => this.refresh(true)); // Preserve expanded state
            },

            markAsChanged: function() {
                const current = JSON.stringify(this.removeExpandedFromTree(this.categories));
                const original = JSON.stringify(this.removeExpandedFromTree(this.originalCategories));
                this.hasUnsavedChanges = current !== original;
            },

            cancelChanges: function() {
                this.categories = JSON.parse(JSON.stringify(this.originalCategories));
                this.hasUnsavedChanges = false;
            },

            clearCategory: function () {
                this.category = {
                  categoryId : null,
                  parentCategoryId : null,
                  name: "",
                  color: 1,
                  cashRegisterVisibility: {
                    visibility: 'visible',
                    from: { hour: 0, minute: 0 },
                    to: { hour: 23, minute: 59 },
                    fromTime: '00:00',
                    toTime: '23:59'
                  },
                  remoteOrderVisibility: {
                    visibility: 'visible',
                    from: { hour: 0, minute: 0 },
                    to: { hour: 23, minute: 59 },
                    fromTime: '00:00',
                    toTime: '23:59'
                  },
                  kioskVisibility: {
                    visibility: 'visible',
                    from: { hour: 0, minute: 0 },
                    to: { hour: 23, minute: 59 },
                    fromTime: '00:00',
                    toTime: '23:59'
                  },
                  allowedUsers: []
                }
            },
            removeCategory: function (categoryId) {
              this.$modal.cs_confirm(this.$t('confirm.delete'),()=>{
                    const vm = this;
                    this.$http.post('/category/delete-category', {categoryId: categoryId})
                        .then(function () {
                            vm.showCategory = false;
                            vm.refresh();
                        });
                });
            },
            prepareEditCategory: function(categoryId) {
                const vm = this;
                vm.categoryImageFile = '';
                vm.navType = 'general';
                this.$http.post('/category/get-category',{categoryId:categoryId})
                    .then(function (response) {
                        vm.category = response.data;
                        if (!vm.category.kioskVisibility) {
                            vm.$set(vm.category, 'kioskVisibility', {
                                visibility: 'visible',
                                from: { hour: 0, minute: 0 },
                                to: { hour: 23, minute: 59 }
                            });
                        }
                        vm.category.kioskVisibility.fromTime = vm.hourMinuteToTimeString(vm.category.kioskVisibility.from);
                        vm.category.kioskVisibility.toTime = vm.hourMinuteToTimeString(vm.category.kioskVisibility.to);
                        vm.category.remoteOrderVisibility.fromTime = vm.hourMinuteToTimeString(vm.category.remoteOrderVisibility.from);
                        vm.category.remoteOrderVisibility.toTime = vm.hourMinuteToTimeString(vm.category.remoteOrderVisibility.to);
                        vm.category.cashRegisterVisibility.fromTime = vm.hourMinuteToTimeString(vm.category.cashRegisterVisibility.from);
                        vm.category.cashRegisterVisibility.toTime = vm.hourMinuteToTimeString(vm.category.cashRegisterVisibility.to);
                        vm.showCategory = true;
                    });
            },
            prepareAddCategory: function() {
                this.clearCategory();
                this.navType = 'general';
                this.showCategory = true;
            },
            addCategory: function() {
                const vm = this;
                const payload = {
                    categoryId: vm.category.categoryId,
                    parentCategoryId: vm.category.parentCategoryId,
                    name: vm.category.name,
                    color: vm.category.color,
                    cashRegisterVisibility: {
                        visibility: vm.category.cashRegisterVisibility.visibility,
                        from: vm.timeStringToHourMinute(vm.category.cashRegisterVisibility.fromTime),
                        to: vm.timeStringToHourMinute(vm.category.cashRegisterVisibility.toTime)
                    },
                    remoteOrderVisibility: {
                        visibility: vm.category.remoteOrderVisibility.visibility,
                        from: vm.timeStringToHourMinute(vm.category.remoteOrderVisibility.fromTime),
                        to: vm.timeStringToHourMinute(vm.category.remoteOrderVisibility.toTime)
                    },
                    kioskVisibility: {
                        visibility: vm.category.kioskVisibility.visibility,
                        from: vm.timeStringToHourMinute(vm.category.kioskVisibility.fromTime),
                        to: vm.timeStringToHourMinute(vm.category.kioskVisibility.toTime)
                    },
                    allowedUsers: vm.category.allowedUsers
                };
                this.$http.post('/category/add-category', payload)
                    .then(function (response) {
                        if(response.status === 200) {
                            vm.refresh();
                            vm.showCategory = false;
                        }
                    });
            },
            editCategory: function() {
                const vm = this;
                const payload = {
                    categoryId: vm.category.categoryId,
                    parentCategoryId: vm.category.parentCategoryId,
                    name: vm.category.name,
                    color: vm.category.color,
                    cashRegisterVisibility: {
                        visibility: vm.category.cashRegisterVisibility.visibility,
                        from: vm.timeStringToHourMinute(vm.category.cashRegisterVisibility.fromTime),
                        to: vm.timeStringToHourMinute(vm.category.cashRegisterVisibility.toTime)
                    },
                    remoteOrderVisibility: {
                        visibility: vm.category.remoteOrderVisibility.visibility,
                        from: vm.timeStringToHourMinute(vm.category.remoteOrderVisibility.fromTime),
                        to: vm.timeStringToHourMinute(vm.category.remoteOrderVisibility.toTime)
                    },
                    kioskVisibility: {
                        visibility: vm.category.kioskVisibility.visibility,
                        from: vm.timeStringToHourMinute(vm.category.kioskVisibility.fromTime),
                        to: vm.timeStringToHourMinute(vm.category.kioskVisibility.toTime)
                    },
                    allowedUsers: vm.category.allowedUsers
                };
                this.$http.post('/category/edit-category', payload)
                    .then(function (response) {
                        if(response.status === 200) {
                            vm.refresh();
                            vm.showCategory = false;
                        }
                    });
            },
            categoriesList: function (excludeId) {
                const ret = [];
                ret.push({
                  categoryId: null,
                  name: ''
                });
                this.flattenCategoriesForList(this.categories, excludeId, ret);
                return ret;
            },

            flattenCategoriesForList: function(categories, excludeId, result) {
                categories.forEach(cat => {
                    if (cat.categoryId !== excludeId) {
                        result.push(cat);
                    }
                    if (cat.children && cat.children.length) {
                        this.flattenCategoriesForList(cat.children, excludeId, result);
                    }
                });
            },
            emojiSelected: function (em) {
              this.showEmoji=false;
              let ti = this.$refs.emojiTarget;
              let startPos = ti.selectionStart,
                endPos = ti.selectionEnd,
                tmpStr = ti.value;

              this.category.name = tmpStr.substring(0, startPos) + em + tmpStr.substring(endPos, tmpStr.length);
            },

            timeStringToHourMinute: function(timeStr) {
                if (!timeStr) return null;
                const parts = timeStr.split(':');
                return {
                    hour: parseInt(parts[0], 10),
                    minute: parseInt(parts[1], 10)
                };
            },

            hourMinuteToTimeString: function(hourMinute) {
                if (!hourMinute) return '';
                const h = String(hourMinute.hour).padStart(2, '0');
                const m = String(hourMinute.minute).padStart(2, '0');
                return h + ':' + m;
            },
            handleCategoryImageUpload: function () {
                const file = this.$refs.categoryImageFile.files[0];
                this.$refs.categoryImageFile.value = '';
                if (!file) return;
                const vm = this;
                this.$refs.categoryImageCropper.open(file).then(function (blob) {
                    if (!blob) return;
                    vm.uploadingCategoryImage = true;
                    const ext = blob.type === 'image/png' ? '.png' : '.jpg';
                    let formData = new FormData();
                    formData.append('file', blob, 'image' + ext);
                    vm.$http.post('/image/upload-category-image?categoryId=' + vm.category.categoryId, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    })
                    .then(function (response) {
                        vm.uploadingCategoryImage = false;
                        if (response.status === 200) {
                            vm.prepareEditCategory(vm.category.categoryId);
                        }
                    }, function () {
                        vm.uploadingCategoryImage = false;
                    });
                });
            },
            deleteCategoryImage: function () {
                const vm = this;
                this.$modal.cs_confirm(this.$t('confirm.deleteCategoryImage'), () => {
                    let formData = new FormData();
                    this.$http.post('/image/upload-category-image?categoryId=' + vm.category.categoryId, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    }).then(function (response) {
                        if (response.status === 200) {
                            vm.prepareEditCategory(vm.category.categoryId);
                        }
                    });
                });
            }
        }
    }
</script>
<template>
    <div class="e-card e-rounding shadow-sm">
        <PremiumBanner/>
        <h1 class="h3">{{ $t('title.categories') }}</h1>
        <p class="text-muted mb-3" style="font-size: 0.95rem;">
            {{ $t('category.dragDropInfo') }}
        </p>

        <Loader v-if="loading" />

        <div v-if="!loading">
            <div class="d-flex flex-wrap-reverse justify-content-between mb-3">
                <div class="d-flex flex-wrap">
                    <button @click="toggleAll" class="btn btn-outline-secondary m-2">
                        <i class="fas pr-1" :class="allExpanded ? 'fa-angle-double-right' : 'fa-angle-double-down'"></i>
                        {{ allExpanded ? $t('button.collapseAll') : $t('button.expandAll') }}
                    </button>
                    <button v-if="hasUnsavedChanges" @click="saveCategoryTree()" class="btn btn-success m-2">
                        <i class="fas fa-save pr-1"></i>{{ $t('button.save') }}
                    </button>
                    <button v-if="hasUnsavedChanges" @click="cancelChanges()" class="btn btn-outline-secondary m-2">
                        <i class="fas fa-times pr-1"></i>{{ $t('button.cancel') }}
                    </button>
                </div>
                <div class="d-flex flex-wrap">
                    <button @click="$router.push('/import-export')" class="btn btn-outline-primary m-2">
                        <i class="fas fa-file-export pr-1"></i>{{ $t('items.exportImport') }}
                    </button>
                    <button @click="prepareAddCategory()" class="btn btn-primary m-2">
                        <i class="fas fa-plus pr-1"></i>{{ $t('button.createCategory') }}
                    </button>
                </div>
            </div>

            <category-tree-item
                v-model="categories"
                @edit-category="prepareEditCategory" />
        </div>
        <div><br></div>

        <div class="e-card-backdrop" v-if="showCategory">
            <div class="row e-modal-row justify-content-center align-items-start">
                <div class="e-modal-card col-10 col-lg-5 bg-white shadow-sm e-rounding p-0">
                    <div class="card-header e-user__header" v-bind:class="['m'+category.color]">
                        <span v-if="category.categoryId === null">{{ $t('title.newCategory') }}</span>
                        <span v-else>{{ $t('title.editCategory') }}</span>
                        <i @click="showCategory = false" class="fas fa-times e-modal-close-icon"></i>
                    </div>
                    <ul class="nav nav-tabs" style="background-color: #cccccc">
                        <li class="nav-item">
                            <a class="nav-link elColor" v-bind:class="{'active':navType==='general','elActive':navType==='general'}" href="#" @click="navType='general'">{{ $t('category.submenu.general') }}</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link elColor" v-bind:class="{'active':navType==='visibility','elActive':navType==='visibility'}" href="#" @click="navType='visibility'">{{ $t('category.submenu.visibility') }}</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link elColor" v-bind:class="{'active':navType==='other','elActive':navType==='other'}" href="#" @click="navType='other'">{{ $t('category.submenu.other') }}</a>
                        </li>
                        <li class="nav-item">
                            <a class="nav-link elColor" v-bind:class="{'active':navType==='image','elActive':navType==='image'}" href="#" @click="navType='image'">{{ $t('item.submenu.image') }}</a>
                        </li>
                    </ul>
                    <div class="card-body py-5 px-4">
                        <div v-if="navType === 'general'">
                        <form>
                            <div class="form-group">
                                <label for="mColor">{{$t('title.selectColor') }}</label>
                                <div style="margin: auto">
                                    <div style="overflow:hidden;" id="mColor">
                                        <div class="m1 mBox" v-bind:class="{mBoxSelected : category.color===1}"
                                                @click="category.color=1"/>
                                        <div class="m2 mBox" v-bind:class="{mBoxSelected : category.color===2}"
                                                @click="category.color=2"/>
                                        <div class="m3 mBox" v-bind:class="{mBoxSelected : category.color===3}"
                                                @click="category.color=3"/>
                                        <div class="m4 mBox" v-bind:class="{mBoxSelected : category.color===4}"
                                                @click="category.color=4"/>
                                        <div class="m5 mBox" v-bind:class="{mBoxSelected : category.color===5}"
                                                @click="category.color=5"/>
                                        <div class="m6 mBox" v-bind:class="{mBoxSelected : category.color===6}"
                                             @click="category.color=6"/>
                                        <div class="m7 mBox" v-bind:class="{mBoxSelected : category.color===7}"
                                             @click="category.color=7"/>
                                        <div class="m8 mBox" v-bind:class="{mBoxSelected : category.color===8}"
                                             @click="category.color=8"/>
                                    </div>
                                </div>
                            </div>
                            <div class="form-group">
                                <label for="text">{{ $t('form.categoryName') }}</label> <a v-show="!showEmoji" href="#" @click="showEmoji=true" style="color: #212529">+🎨</a>
                                <input ref="emojiTarget" v-model="category.name" class="form-control" id="text">
                                <Emoji v-if="showEmoji" @emoji="emojiSelected"></Emoji>
                            </div>
                            <div class="form-group">
                             <label for="parentCategory">{{ $t('form.parentCategoryName') }}</label>
                              <select v-model="category.parentCategoryId" class="form-control" id="parentCategory">
                              <option v-for="c in categoriesList(category.categoryId)" v-bind:value="c.categoryId"
                                      v-bind:key="c.categoryId">
                                {{ c.name }}
                              </option>
                            </select>
                            </div>

                        </form>
                        </div>
                        <div v-if="navType === 'visibility'">
                            <!-- Vzdálené objednávky -->
                            <div class="form-group">
                              <label><strong>{{ $t('category.remoteOrderVisibility') }}</strong></label>
                              <div class="btn-group btn-group-toggle btn-group-sm w-100 d-flex" data-toggle="buttons">
                                <label class="btn btn-sm flex-fill" :class="category.remoteOrderVisibility.visibility === 'invisible' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.remoteOrderVisibility.visibility = 'invisible'">
                                  {{ $t('category.visibility.invisible') }}
                                </label>
                                <label class="btn btn-sm flex-fill" :class="category.remoteOrderVisibility.visibility === 'visible' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.remoteOrderVisibility.visibility = 'visible'">
                                  {{ $t('category.visibility.visible') }}
                                </label>
                                <label class="btn btn-sm flex-fill" :class="category.remoteOrderVisibility.visibility === 'scheduled' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.remoteOrderVisibility.visibility = 'scheduled'">
                                  {{ $t('category.visibility.scheduled') }}
                                </label>
                              </div>
                              <div v-if="category.remoteOrderVisibility.visibility === 'scheduled'" class="mt-3">
                                <div class="input-group input-group-sm">
                                  <div class="input-group-prepend">
                                    <span class="input-group-text">{{ $t('remoteOrder.schedule.timeFrom') }}</span>
                                  </div>
                                  <input type="time" class="form-control" v-model="category.remoteOrderVisibility.fromTime">
                                  <div class="input-group-prepend">
                                    <span class="input-group-text">{{ $t('remoteOrder.schedule.timeTo') }}</span>
                                  </div>
                                  <input type="time" class="form-control" v-model="category.remoteOrderVisibility.toTime">
                                </div>
                              </div>
                            </div>

                            <!-- Na pokladně -->
                            <div class="form-group">
                              <label><strong>{{ $t('category.cashRegisterVisibility') }}</strong></label>
                              <div class="btn-group btn-group-toggle btn-group-sm w-100 d-flex" data-toggle="buttons">
                                <label class="btn btn-sm flex-fill" :class="category.cashRegisterVisibility.visibility === 'invisible' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.cashRegisterVisibility.visibility = 'invisible'">
                                  {{ $t('category.visibility.invisible') }}
                                </label>
                                <label class="btn btn-sm flex-fill" :class="category.cashRegisterVisibility.visibility === 'visible' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.cashRegisterVisibility.visibility = 'visible'">
                                  {{ $t('category.visibility.visible') }}
                                </label>
                                <label class="btn btn-sm flex-fill" :class="category.cashRegisterVisibility.visibility === 'scheduled' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.cashRegisterVisibility.visibility = 'scheduled'">
                                  {{ $t('category.visibility.scheduled') }}
                                </label>
                              </div>
                              <div v-if="category.cashRegisterVisibility.visibility === 'scheduled'" class="mt-3">
                                <div class="input-group input-group-sm">
                                  <div class="input-group-prepend">
                                    <span class="input-group-text">{{ $t('remoteOrder.schedule.timeFrom') }}</span>
                                  </div>
                                  <input type="time" class="form-control" v-model="category.cashRegisterVisibility.fromTime">
                                  <div class="input-group-prepend">
                                    <span class="input-group-text">{{ $t('remoteOrder.schedule.timeTo') }}</span>
                                  </div>
                                  <input type="time" class="form-control" v-model="category.cashRegisterVisibility.toTime">
                                </div>
                              </div>
                            </div>

                            <!-- Kiosk -->
                            <div class="form-group">
                              <label><strong>{{ $t('category.kioskVisibility') }}</strong></label>
                              <div class="btn-group btn-group-toggle btn-group-sm w-100 d-flex" data-toggle="buttons">
                                <label class="btn btn-sm flex-fill" :class="category.kioskVisibility.visibility === 'invisible' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.kioskVisibility.visibility = 'invisible'">
                                  {{ $t('category.visibility.invisible') }}
                                </label>
                                <label class="btn btn-sm flex-fill" :class="category.kioskVisibility.visibility === 'visible' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.kioskVisibility.visibility = 'visible'">
                                  {{ $t('category.visibility.visible') }}
                                </label>
                                <label class="btn btn-sm flex-fill" :class="category.kioskVisibility.visibility === 'scheduled' ? 'btn-primary' : 'btn-outline-primary'" @click.prevent="category.kioskVisibility.visibility = 'scheduled'">
                                  {{ $t('category.visibility.scheduled') }}
                                </label>
                              </div>
                              <div v-if="category.kioskVisibility.visibility === 'scheduled'" class="mt-3">
                                <div class="input-group input-group-sm">
                                  <div class="input-group-prepend">
                                    <span class="input-group-text">{{ $t('remoteOrder.schedule.timeFrom') }}</span>
                                  </div>
                                  <input type="time" class="form-control" v-model="category.kioskVisibility.fromTime">
                                  <div class="input-group-prepend">
                                    <span class="input-group-text">{{ $t('remoteOrder.schedule.timeTo') }}</span>
                                  </div>
                                  <input type="time" class="form-control" v-model="category.kioskVisibility.toTime">
                                </div>
                              </div>
                            </div>
                        </div>
                        <div v-if="navType === 'other'">
                            <div class="form-group">
                                <label>{{ $t('category.allowedUsers') }}</label>
                                <div class="border rounded p-2" style="max-height: 220px; overflow-y: auto;">
                                    <div v-if="users.length === 0" class="text-muted small p-1">{{ $t('category.allowedUsersEmpty') }}</div>
                                    <div v-for="u in users" :key="u.userId" class="custom-control custom-checkbox py-1">
                                        <input type="checkbox" class="custom-control-input"
                                               :id="'allowed-user-' + u.userId"
                                               :value="u.userId"
                                               v-model="category.allowedUsers">
                                        <label class="custom-control-label" :for="'allowed-user-' + u.userId">{{ u.userName }}</label>
                                    </div>
                                </div>
                                <small class="form-text text-muted">{{ $t('category.allowedUsersInfo') }}</small>
                            </div>
                        </div>
                        <div v-if="navType === 'image'">
                            <div v-if="category.categoryId === null" class="alert alert-info">
                                {{ $t('category.imageSaveFirst') }}
                            </div>
                            <div v-else>
                                <div v-if="category.imageId != null" class="mb-3">
                                    <img :src="bu + '/image/get-image?imageId=' + category.imageId + '&ak=' + ak" class="category-image-preview mb-2"/>
                                </div>
                                <p class="text-muted"><i class="fas fa-info-circle mr-1"></i>{{ $t('category.imageInfo') }}</p>
                                <input type="file" ref="categoryImageFile" accept="image/png, image/jpeg" v-on:change="handleCategoryImageUpload()" style="display:none"/>
                                <ImageCropper ref="categoryImageCropper"/>
                                <div>
                                    <button type="button" @click="$refs.categoryImageFile.click()" class="btn btn-primary btn-sm mr-2" :disabled="uploadingCategoryImage">
                                        <i class="fa fa-upload pr-1"></i>{{ $t('button.uploadCategoryImage') }}
                                    </button>
                                    <button v-if="category.imageId != null" type="button" @click="deleteCategoryImage()" class="btn btn-outline-danger btn-sm" :disabled="uploadingCategoryImage">
                                        <i class="fas fa-trash pr-1"></i>{{ $t('button.deleteCategoryImage') }}
                                    </button>
                                    <i v-if="uploadingCategoryImage" class="fas fa-cog fa-spin ml-2"></i>
                                </div>
                            </div>
                        </div>
                        <div class="d-flex flex-wrap-reverse justify-content-between pt-3">
                            <div>
                                <button v-if="category.categoryId !== null && navType === 'general'" @click="removeCategory(category.categoryId)" class="btn btn-danger mr-4">
                                  {{ $t('button.deleteCategory') }}
                                </button>
                            </div>
                            <div class="e-btn-mobile-container">
                                <button @click="showCategory = false" class="btn btn-outline-secondary mr-4">
                                    {{ $t('button.back')}}
                                </button>
                                <button v-if="category.categoryId === null && navType !== 'image'" @click="addCategory()" class="btn btn-primary">
                                  {{ $t('button.createCategory')}}
                                </button>
                                <button v-if="category.categoryId !== null && navType !== 'image'" @click="editCategory()" class="btn btn-primary">
                                    {{ $t('button.save') }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
    .m1 {
      background-color: #8ea459;
    }
    .m2 {
      background-color: #7ac4dd;
    }
    .m3 {
      background-color: #7aa1ec;
    }
    .m4 {
      background-color: #f0e071;
    }
    .m5 {
      background-color: #e49d5b;
    }
    .m6 {
      background-color: #b191dc;
    }
    .m7 {
      background-color: #d9736f;
    }
    .m8 {
      background-color: #ceb19f;
    }

    .mBox {
        display: inline-block;
        width: 30px;
        height: 30px;
        margin: 3px;
        border-radius: .25rem;
        cursor: pointer;
    }

    .e-mcode-square {
        width: max-content;
        padding: .5rem;
        border-radius: .25rem;
        font-weight: 600;
        text-align: center;
        cursor: pointer;
    }

    .mBoxSelected {
        border: 2px solid black;
    }

    .table td {
        vertical-align: middle;
        word-break: break-word
    }

    .price {
        text-align: right;
    }

    .category-image-preview {
        max-width: 200px;
        max-height: 200px;
        border: 1px solid #ddd;
        border-radius: .25rem;
        padding: 4px;
        display: block;
    }

    @media screen and (max-width: 576px) {
        .e-btn-mobile-container {
            width: 100%;
            text-align: right;
            margin-bottom: 2rem;
        }
    }
</style>